import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../context/AuthContext";

const B    = "#2C1A0E";
const ROSE = "#C9847A";

const STATUS_CONFIG = {
  "not-started": { label: "Not Started", bg: "#F3F4F6", color: "#6B7280" },
  "on-track":    { label: "On Track",    bg: "#DCFCE7", color: "#15803D" },
  "at-risk":     { label: "At Risk",     bg: "#FEF9C3", color: "#854D0E" },
  "behind":      { label: "Behind",      bg: "#FEE2E2", color: "#DC2626" },
  "done":        { label: "Done ✓",      bg: "#DBEAFE", color: "#1D4ED8" },
};

const TAG_COLOR = {
  REVENUE:     { bg: "#FEF9C3", color: "#854D0E" },
  INVENTORY:   { bg: "#DCFCE7", color: "#15803D" },
  "NEW STREAMS":{ bg: "#EDE9FE", color: "#6D28D9" },
  PLANNING:    { bg: "#DBEAFE", color: "#1D4ED8" },
};

export default function OKRsPage() {
  const [okrs,    setOkrs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [quarter, setQuarter] = useState("Q1");
  const [editing, setEditing] = useState(null); // okr _id
  const [edits,   setEdits]   = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiFetch(`/api/okrs?quarter=${quarter}`);
    if (r.ok) setOkrs(await r.json());
    setLoading(false);
  }, [quarter]);

  useEffect(() => { load(); }, [load]);

  // Group by objective
  const objectives = {};
  okrs.forEach(kr => {
    const key = `${kr.objective_index}`;
    if (!objectives[key]) objectives[key] = {
      title: kr.objective_title,
      tag:   kr.objective_tag,
      krs:   [],
    };
    objectives[key].krs.push(kr);
  });

  const saveKr = async (kr) => {
    const update = edits[kr._id] || {};
    await apiFetch(`/api/okrs/${kr._id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(update),
    });
    setEditing(null);
    setEdits({});
    load();
  };

  const setEdit = (id, field, val) => {
    setEdits(e => ({ ...e, [id]: { ...(e[id] || {}), [field]: val } }));
  };

  const periodLabels = { Q1: "Apr–Jun 2026", Q2: "Jul–Sep 2026", Q3: "Oct–Dec 2026", Q4: "Jan–Mar 2027" };

  // Compute quarter progress
  const totalKrs    = okrs.length;
  const doneKrs     = okrs.filter(k => k.status === "done").length;
  const onTrackKrs  = okrs.filter(k => k.status === "on-track").length;
  const atRiskKrs   = okrs.filter(k => k.status === "at-risk").length;
  const behindKrs   = okrs.filter(k => k.status === "behind").length;

  return (
    <div style={{ padding: "28px 28px 80px", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                       color: B, fontSize: 26, fontWeight: 700, margin: 0 }}>
            🎯 OKRs
          </h1>
          <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            FY27 Objectives & Key Results · Click a KR to update actuals
          </p>
        </div>
        {/* Quarter tabs */}
        <div style={{ display: "flex", gap: 6 }}>
          {["Q1","Q2","Q3","Q4"].map(q => (
            <button key={q} onClick={() => setQuarter(q)} style={{
              background: quarter === q ? B : "#F0EBE3",
              color: quarter === q ? "#FAF7F2" : B,
              border: "none", borderRadius: 7, padding: "7px 16px",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}>{q}</button>
          ))}
        </div>
      </div>

      {/* Quarter label + progress */}
      <div style={{ background: "#fff", border: "1px solid #EDE0D4", borderRadius: 12,
                    padding: "16px 20px", marginBottom: 24,
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: B }}>{quarter} · {periodLabels[quarter]}</div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{totalKrs} Key Results</div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          {[
            ["Done",       doneKrs,    "#1D4ED8"],
            ["On Track",   onTrackKrs, "#15803D"],
            ["At Risk",    atRiskKrs,  "#854D0E"],
            ["Behind",     behindKrs,  "#DC2626"],
          ].map(([label, count, color]) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color }}>{count}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{label}</div>
            </div>
          ))}
        </div>
        {/* Progress bar */}
        <div style={{ width: "100%", background: "#F0EBE3", borderRadius: 6, height: 8, overflow: "hidden" }}>
          <div style={{ display: "flex", height: "100%" }}>
            <div style={{ width: `${(doneKrs/totalKrs)*100}%`, background: "#1D4ED8" }} />
            <div style={{ width: `${(onTrackKrs/totalKrs)*100}%`, background: "#15803D" }} />
            <div style={{ width: `${(atRiskKrs/totalKrs)*100}%`, background: "#F59E0B" }} />
            <div style={{ width: `${(behindKrs/totalKrs)*100}%`, background: "#DC2626" }} />
          </div>
        </div>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 13 }}>Loading…</div>}

      {/* Objectives */}
      {!loading && Object.values(objectives).map((obj, oi) => {
        const tagCfg = TAG_COLOR[obj.tag] || { bg: "#F3F4F6", color: "#6B7280" };
        return (
          <div key={oi} style={{ marginBottom: 28 }}>
            {/* Objective header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: B }}>{oi + 1}.</span>
              <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                            fontSize: 18, fontWeight: 700, color: B }}>{obj.title}</div>
              <span style={{ background: tagCfg.bg, color: tagCfg.color,
                             borderRadius: 5, padding: "2px 10px",
                             fontSize: 10, fontWeight: 800, letterSpacing: 1 }}>
                {obj.tag}
              </span>
            </div>

            {/* KR cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingLeft: 24 }}>
              {obj.krs.map(kr => {
                const sc     = STATUS_CONFIG[kr.status] || STATUS_CONFIG["not-started"];
                const isEdit = editing === kr._id;
                const vals   = edits[kr._id] || {};
                const months = ["m1_actual","m2_actual","m3_actual"];
                const mLabels = quarter === "Q1" ? ["Apr","May","Jun"]
                              : quarter === "Q2" ? ["Jul","Aug","Sep"]
                              : quarter === "Q3" ? ["Oct","Nov","Dec"]
                              :                   ["Jan","Feb","Mar"];
                return (
                  <div key={kr._id} style={{
                    background: "#fff", border: "1px solid #EDE0D4", borderRadius: 10,
                    padding: "14px 16px", cursor: "pointer",
                    borderLeft: `3px solid ${sc.color}`,
                  }} onClick={() => { setEditing(isEdit ? null : kr._id); }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                                  alignItems: "flex-start", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: B, marginBottom: 4 }}>
                          {kr.kr_label}
                        </div>
                        <div style={{ fontSize: 11, color: "#888", display: "flex", gap: 16 }}>
                          <span>📏 {kr.metric}</span>
                          <span>👤 {kr.owner}</span>
                          <span>🎯 {kr.target}</span>
                        </div>
                      </div>
                      <span style={{ background: sc.bg, color: sc.color, borderRadius: 5,
                                     padding: "3px 10px", fontSize: 11, fontWeight: 600,
                                     whiteSpace: "nowrap" }}>
                        {sc.label}
                      </span>
                    </div>

                    {/* Monthly actuals (always visible) */}
                    <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                      {months.map((mf, i) => (
                        <div key={mf} style={{ flex: 1 }}>
                          <div style={{ fontSize: 10, color: "#aaa", marginBottom: 2 }}>{mLabels[i]}</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: kr[mf] ? B : "#ccc" }}>
                            {kr[mf] || "—"}
                          </div>
                        </div>
                      ))}
                      {kr.notes && (
                        <div style={{ flex: 2, fontSize: 11, color: "#888",
                                      borderLeft: "1px solid #EDE0D4", paddingLeft: 12 }}>
                          {kr.notes}
                        </div>
                      )}
                    </div>

                    {/* Edit form */}
                    {isEdit && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #EDE0D4" }}
                           onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                          {months.map((mf, i) => (
                            <div key={mf}>
                              <label style={{ fontSize: 10, color: "#888", display: "block", marginBottom: 3 }}>
                                {mLabels[i]} actual
                              </label>
                              <input
                                defaultValue={kr[mf] || ""}
                                onChange={e => setEdit(kr._id, mf, e.target.value)}
                                style={inp}
                                placeholder="e.g. ₹12,000"
                              />
                            </div>
                          ))}
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 10, color: "#888", display: "block", marginBottom: 3 }}>
                              Status
                            </label>
                            <select
                              defaultValue={kr.status}
                              onChange={e => setEdit(kr._id, "status", e.target.value)}
                              style={inp}>
                              {Object.entries(STATUS_CONFIG).map(([v, cfg]) => (
                                <option key={v} value={v}>{cfg.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontSize: 10, color: "#888", display: "block", marginBottom: 3 }}>
                            Notes
                          </label>
                          <textarea
                            defaultValue={kr.notes || ""}
                            onChange={e => setEdit(kr._id, "notes", e.target.value)}
                            style={{ ...inp, width: "100%", height: 56, resize: "vertical" }}
                            placeholder="Any context or blockers…"
                          />
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => saveKr(kr)} style={btnPrimary}>Save</button>
                          <button onClick={() => { setEditing(null); setEdits({}); }}
                            style={btnSecondary}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const inp = { border:"1px solid #DDD4C8", borderRadius:6, padding:"6px 10px",
              fontSize:12, background:"#FAF7F2", color:"#2C1A0E" };
const btnPrimary  = { background:"#2C1A0E", color:"#FAF7F2", border:"none",
                      borderRadius:7, padding:"7px 18px", fontSize:12,
                      fontWeight:600, cursor:"pointer" };
const btnSecondary= { background:"#F0EBE3", color:"#2C1A0E", border:"1px solid #DDD4C8",
                      borderRadius:7, padding:"7px 14px", fontSize:12, cursor:"pointer" };
